import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n";
import { errorKey } from "../lib/errors";
import { Icon } from "./Icon";
import { useFeedback } from "./Toast";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog: aria-modal, labelled by its title, initial focus ([data-autofocus] or the first
 * field), Tab stays inside, Escape closes (unless `dismissible` is false), focus returns to the
 * opener on close. Clicking the backdrop never closes it, so typed input or one-time secrets
 * are not lost by a stray click.
 */
export function Dialog({
  title,
  description,
  onClose,
  children,
  footer,
  onSubmit,
  wide = false,
  dismissible = true,
}: {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Render the body and footer inside a <form> so Enter submits. */
  onSubmit?: (e: FormEvent) => void;
  wide?: boolean;
  dismissible?: boolean;
}) {
  const t = useT();
  const titleId = useId();
  const descId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const box = ref.current;
    if (box) {
      const inBody = FOCUSABLE.split(", ")
        .map((sel) => `.dialog-body ${sel}`)
        .join(", ");
      const target = box.querySelector<HTMLElement>("[data-autofocus]") ?? box.querySelector<HTMLElement>(inBody) ?? box;
      target.focus();
    }
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && dismissible) {
      e.stopPropagation();
      closeRef.current();
      return;
    }
    if (e.key !== "Tab" || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const inner = (
    <>
      <div className="dialog-body">{children}</div>
      {footer && <div className="dialog-footer">{footer}</div>}
    </>
  );

  return createPortal(
    <div className="dialog-backdrop">
      <div
        ref={ref}
        className={`dialog${wide ? " dialog-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="dialog-header">
          <div>
            <h2 id={titleId} className="dialog-title">
              {title}
            </h2>
            {description && (
              <p id={descId} className="dialog-description">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <button type="button" className="icon-button" onClick={onClose} title={t("common.close")}>
              <Icon name="close" size={18} label={t("common.close")} />
            </button>
          )}
        </header>
        {onSubmit ? (
          <form onSubmit={onSubmit} noValidate className="dialog-form">
            {inner}
          </form>
        ) : (
          inner
        )}
      </div>
    </div>,
    document.body,
  );
}

/** A confirm step for destructive or consequential actions; shows provider errors inline. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  danger = false,
  successMessage,
  onConfirm,
  onClose,
}: {
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Toast shown after `onConfirm` resolves; errors stay inline in the dialog. */
  successMessage?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  const feedback = useFeedback();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      if (successMessage) feedback.ok(successMessage);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="button" className={`button ${danger ? "button-danger" : "button-primary"}`} onClick={() => void run()} disabled={busy} data-autofocus>
            {busy ? t("common.working") : confirmLabel}
          </button>
        </>
      }
    >
      <div className="dialog-text">{children}</div>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

export function FormError({ error }: { error: unknown }) {
  const t = useT();
  return (
    <p className="form-error" role="alert">
      {t(errorKey(error))}
    </p>
  );
}

/** Label + control + optional hint and inline error, wired with aria-describedby by id convention. */
export function FormField({
  id,
  label,
  hint,
  error,
  optional = false,
  children,
}: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optional && <span className="field-optional"> · {t("form.optional")}</span>}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="field-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

/** aria props for a control inside FormField. */
export function fieldAria(id: string, opts: { hint?: unknown; error?: string | null }) {
  const ids = [opts.hint ? `${id}-hint` : null, opts.error ? `${id}-error` : null].filter(Boolean).join(" ");
  return { id, "aria-invalid": opts.error ? true : undefined, "aria-describedby": ids || undefined } as const;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for browsers without the async clipboard API (or without permission).
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}

export function CopyButton({ text, label, compact = false }: { text: string; label: string; compact?: boolean }) {
  const t = useT();
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const h = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(h);
  }, [state]);
  const text2 = state === "done" ? t("common.copied") : state === "failed" ? t("common.copyFailed") : compact ? null : t("common.copy");
  return (
    <button
      type="button"
      className={compact ? "icon-button copy-button" : "button button-secondary button-sm"}
      onClick={async () => setState((await copyText(text)) ? "done" : "failed")}
      title={label}
      aria-label={label}
    >
      <Icon name={state === "done" ? "check" : "copy"} size={16} />
      {text2 && <span aria-live="polite">{text2}</span>}
    </button>
  );
}
